import { Box, Button, ClickAwayListener, FormControl, FormControlLabel, Grid, Grow, IconButton, InputLabel, ListItemText, MenuList, OutlinedInput, Paper, Popper, styled, Switch, Typography, useControlled } from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import { range, sumBy } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatString, _T } from '../../../include/appI18n';
import { isGagraUI } from '../../../include/react-common/theme';
import { BootstrapInput } from '../../../include/react-components/BootstrapInput';
import { pluralizeAutoSplit } from '../../../include/utils';
import { useDropdownStyles } from '../../NewTourSearch/compoments/ChildAccommodationSelector';
import { TpArrowIcon2, TpFormControl, TpInput, TpLabel, TpMenuItem } from '../../NewTourSearch/compoments/tp';
export const defaultAccommodation = {
    adults: 2,
    childs: 0,
    childAccommodations: []
};
const IconButton2 = styled(IconButton)(({ theme }) => ({
    padding: '8px',
}));
function withDropdownArrowClass(adornment, open) {
    var _a, _b;
    if (!React.isValidElement(adornment))
        return adornment;
    const element = adornment;
    const classes = `${(_b = (_a = element.props) === null || _a === void 0 ? void 0 : _a.className) !== null && _b !== void 0 ? _b : ''} tp-dropdown-arrow${open ? ' tp-dropdown-arrow-open' : ''}`.trim();
    return React.cloneElement(element, { className: classes });
}
export const AccommodationSelector = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e;
    const [value /*, onChange*/] = useControlled({
        name: 'value',
        controlled: props.value,
        default: props.multiply
            ? [Object.assign({}, defaultAccommodation)]
            : Object.assign({}, defaultAccommodation),
        state: 'value',
    });
    const totalAdults = useMemo(() => {
        const adults = Array.isArray(value) ? sumBy(value, a => a.adults) : value.adults;
        return adults;
    }, [value]);
    const totalChilds = useMemo(() => {
        const childs = Array.isArray(value) ? sumBy(value, a => a.childAccommodations.length) : value.childAccommodations.length;
        return childs;
    }, [value]);
    const isOpenControlled = typeof props.open === 'boolean';
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = isOpenControlled ? !!props.open : internalOpen;
    const setOpen = (next) => {
        const resolved = typeof next === 'function' ? next(open) : next;
        if (!isOpenControlled)
            setInternalOpen(resolved);
        if (props.onOpenChange)
            props.onOpenChange(resolved);
    };
    const [showDropdownContent, setShowDropdownContent] = React.useState(false);
    const anchorRef = useRef(null);
    const menuRef = React.useRef(null);
    const handleToggle = () => {
        if (!props.disabled)
            setOpen((prevOpen) => !prevOpen);
        else
            setOpen(false);
    };
    const handleClose = (event) => {
        if (anchorRef.current && menuRef.current && menuRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    const prevOpen = useRef(open);
    // return focus to the button when we transitioned from !open -> open
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);
    useEffect(() => {
        setTimeout(() => setShowDropdownContent(open), 200);
    }, [open]);
    const valueLabel = useMemo(() => {
        return `${totalAdults + totalChilds} ${pluralizeAutoSplit(totalAdults + totalChilds, _T.COMMON_PERSONS)}`;
    }, [totalAdults, totalChilds]);
    const resolvedEndAdornment = useMemo(() => {
        var _a;
        const adornment = (_a = props.endAdornment) !== null && _a !== void 0 ? _a : React.createElement(TpArrowIcon2, { className: "MuiSvgIcon-root tp-dropdown-arrow" });
        return withDropdownArrowClass(adornment, open);
    }, [props.endAdornment, open]);
    return (React.createElement(Box, Object.assign({}, ((_a = props.boxProps) !== null && _a !== void 0 ? _a : {}), { height: '100%', component: 'div' }),
        React.createElement("div", { style: { display: 'flex', height: '100%' }, ref: ref },
            React.createElement("div", { style: { display: 'flex', width: '100%', height: '100%' }, ref: anchorRef },
                props.useBootstrapInput ? React.createElement(React.Fragment, null,
                    React.createElement(FormControl, { variant: "filled", fullWidth: true, disabled: props.disabled },
                        props.label ? React.createElement(InputLabel, { error: props.error }, props.label) : null,
                        React.createElement(BootstrapInput, { disabled: props.disabled, value: valueLabel, onInput: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }, readOnly: true, "aria-controls": open ? 'menu-list-grow' : undefined, "aria-haspopup": "true", onClick: handleToggle, startAdornment: props.startAdornment, endAdornment: resolvedEndAdornment, inputProps: { style: { caretColor: 'transparent', cursor: 'pointer' } }, error: props.error }))) : React.createElement(React.Fragment, null,
                    React.createElement(TpFormControl, { variant: "filled", classes: props.formControlClasses, fullWidth: true, disabled: props.disabled },
                        props.label ? React.createElement(TpLabel, { error: props.error }, props.label) : null,
                        React.createElement(TpInput, { classes: props.inputClasses, disabled: props.disabled, value: valueLabel, onInput: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }, readOnly: true, "aria-controls": open ? 'menu-list-grow' : undefined, "aria-haspopup": "true", onClick: handleToggle, startAdornment: props.startAdornment, endAdornment: resolvedEndAdornment, inputProps: { style: { caretColor: 'transparent', cursor: 'pointer' } }, error: props.error }))),
                React.createElement(Popper, { className: 'mui-popper-dropdown', placement: 'bottom-end', open: open && !!anchorRef.current, anchorEl: anchorRef.current, role: undefined, transition: true, style: {
                        zIndex: 9999,
                        width: ((_c = (_b = anchorRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) !== null && _c !== void 0 ? _c : 0) < 250 ? 250 : ((_e = (_d = anchorRef.current) === null || _d === void 0 ? void 0 : _d.clientWidth) !== null && _e !== void 0 ? _e : 250),
                        minWidth: '250px',
                        maxHeight: '300px',
                        paddingTop: '2px',
                        paddingBottom: '2px',
                    } }, ({ TransitionProps, placement }) => (React.createElement(Grow, Object.assign({}, TransitionProps, { style: {
                        transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                        marginTop: placement === 'bottom' ? '2px' : 0,
                        marginBottom: placement !== 'bottom' ? '2px' : 0,
                    } }),
                    React.createElement(Paper, { elevation: 4 },
                        React.createElement(ClickAwayListener, { onClickAway: handleClose },
                            React.createElement(Box, { width: "100%", height: "100%" },
                                React.createElement(AccommodationSelectorStatic, Object.assign({}, props, { handleClose: handleClose, visible: showDropdownContent || !props.dropdownDelay }))))))))))));
});
export const AccommodationSelectorStatic = React.forwardRef((props, ref) => {
    var _a;
    const classes = useDropdownStyles();
    const color = isGagraUI ? 'primary' : 'secondary';
    const { onChange, handleClose, visible } = props;
    const [value /*, onChange*/] = useControlled({
        name: 'value',
        controlled: props.value,
        default: props.multiply
            ? [Object.assign({}, defaultAccommodation)]
            : Object.assign({}, defaultAccommodation),
        state: 'value',
    });
    const [totalAdults, allowRemoveAdults, allowAddAdults] = useMemo(() => {
        const adults = Array.isArray(value) ? sumBy(value, a => a.adults) : value.adults;
        return [adults, adults > props.minAdults, adults < props.maxAdults];
    }, [value, props.minAdults, props.maxAdults]);
    const [totalChilds, allowRemoveChilds, allowAddChilds] = useMemo(() => {
        const childs = Array.isArray(value) ? sumBy(value, a => a.childAccommodations.length) : value.childAccommodations.length;
        return [childs, childs > props.minChilds, childs < props.maxChilds];
    }, [value, props.maxChilds, props.maxChilds]);
    return (React.createElement("div", { ref: ref, style: { width: "100%", height: "100%", display: visible ? undefined : 'none' } },
        React.createElement(Box, { width: "100%", height: "100%", padding: 2 },
            props.multiply && Array.isArray(value)
                ? React.createElement(NumberInput, { style: { marginBottom: '8px' }, value: value.length, onChange: newValue => {
                        if (newValue > value.length) {
                            let newArray = [...value, ...range(0, newValue - value.length).map(_ => (Object.assign({}, defaultAccommodation)))];
                            onChange(newArray);
                        }
                        else if (newValue < value.length) {
                            let newArray = [...value].slice(0, newValue);
                            onChange(newArray);
                        }
                    }, minValue: 1, maxValue: (_a = props.maxItems) !== null && _a !== void 0 ? _a : 4, decrementDisabled: !allowRemoveAdults && !allowRemoveChilds, incrementDisabled: !allowAddAdults && !allowAddChilds, label: props.itemsCountInputLabel, color: color })
                : null,
            (Array.isArray(value) ? value : [value]).map((accommodation, index, accommodations) => {
                var _a;
                return (React.createElement(Box, { width: "100%", marginTop: "24px" },
                    props.multiply && props.itemGroupLabelFormat
                        ? React.createElement(Typography, { variant: "h5", style: { marginBottom: '16px' }, className: "MuiTypography-h5-widget" }, formatString(props.itemGroupLabelFormat, [index + 1]))
                        : null,
                    React.createElement(NumberInput, { style: { marginBottom: '8px' }, value: accommodation.adults, onChange: newValue => {
                            let newAccommodation = Object.assign(Object.assign({}, accommodation), { adults: newValue });
                            if (!props.multiply)
                                onChange(newAccommodation);
                            else if (Array.isArray(value)) {
                                let newArray = [...value.slice(0, index), newAccommodation, ...value.slice(index + 1)];
                                onChange(newArray);
                            }
                        }, minValue: props.minAdults, maxValue: props.maxAdults, label: _T.COMMON_ADULTS, color: color }),
                    accommodation.childAccommodations.map((child, childIndex) => (React.createElement(Box, { justifyContent: "space-between", display: "flex", alignItems: "center", fontSize: "1.1em", marginBottom: "8px" },
                        React.createElement(Box, null,
                            React.createElement(Typography, null,
                                _T.COMMON_CHILD,
                                ", ",
                                child.age,
                                " ",
                                pluralizeAutoSplit(child.age, _T.COMMON_AGES_PLURAL))),
                        React.createElement(Box, null,
                            React.createElement(IconButton2, { onClick: _ => {
                                    let newArray = [...accommodation.childAccommodations.slice(0, childIndex), ...accommodation.childAccommodations.slice(childIndex + 1)];
                                    let newAccommodation = Object.assign(Object.assign({}, accommodation), { childs: newArray.length, childAccommodations: newArray });
                                    if (!props.multiply)
                                        onChange(newAccommodation);
                                    else if (Array.isArray(value)) {
                                        let newArray = [...value.slice(0, index), newAccommodation, ...value.slice(index + 1)];
                                        onChange(newArray);
                                    }
                                } },
                                React.createElement(RemoveCircle, { className: "MuiSvgIcon-root MuiSvgIcon-root-widget", color: color })))))),
                    accommodation.childAccommodations.length < props.maxChilds ? React.createElement(CustomSelect, { disabled: accommodation.childAccommodations.length >= props.maxChilds, label: _T.COMMON_ADD_CHILD, value: undefined, options: range(0, props.maxChildAge + 1), valueSelector: x => x, textSelector: x => `${x} ${pluralizeAutoSplit(x, _T.COMMON_AGES_PLURAL)}`, onChange: (newValue) => {
                            let newArray = [...accommodation.childAccommodations, { age: typeof newValue == "number" ? newValue : Number.parseInt(newValue), withPlace: true }];
                            let newAccommodation = Object.assign(Object.assign({}, accommodation), { childs: newArray.length, childAccommodations: newArray });
                            if (!props.multiply)
                                onChange(newAccommodation);
                            else if (Array.isArray(value)) {
                                let newArray = [...value.slice(0, index), newAccommodation, ...value.slice(index + 1)];
                                onChange(newArray);
                            }
                        }, startAdornment: React.createElement(IconButton2, { color: color, disabled: true },
                            React.createElement(AddCircle, { className: "MuiSvgIcon-root MuiSvgIcon-root-widget", color: color })), endAdornment: React.createElement(TpArrowIcon2, { className: "MuiSvgIcon-root" }), boxProps: { style: { marginBottom: '8px' } }, color: color }) : null,
                    props.showChildsWithPlaceOnlySwitch && accommodation.childAccommodations.length > 0 ? React.createElement(React.Fragment, null,
                        React.createElement(Grid, { item: true, xs: 12, className: classes.switchRow },
                            React.createElement(FormControlLabel, { control: React.createElement(Switch, { color: "primary", checked: !!props.childsWithPlaceOnly, onChange: (e) => props.onChildsWithPlaceOnlyChange && props.onChildsWithPlaceOnlyChange(e.target.checked) }), label: (_a = props.childsWithPlaceOnlyLabel) !== null && _a !== void 0 ? _a : _T.NEW_TOUR_SEARCH_CHILD_WITH_PLACE_ONLY_LABEL }))) : null));
            }),
            React.createElement(Button, { onClick: handleClose, variant: "contained", color: color, style: { marginTop: '8px' } }, "OK"))));
});
export function NumberInput(props) {
    var _a, _b, _c, _d;
    const { onChange } = props;
    const [value /*, onChange*/] = useControlled({
        name: 'value',
        controlled: props.value,
        default: 0,
        state: 'value',
    });
    return (React.createElement(FormControl, { variant: "outlined", fullWidth: true, disabled: props.disabled, style: props.style },
        props.label ? React.createElement(InputLabel, { variant: "outlined", error: props.error }, props.label) : null,
        React.createElement(OutlinedInput, { label: props.label, disabled: props.disabled, value: value, onInput: (e) => {
                const input = e.target;
                const v = Number.parseInt(input.value);
                if (!Number.isInteger(v) || (v < value && props.decrementDisabled) || (v > value && props.incrementDisabled)) {
                    // возможно лучше всегда отменять
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                onChange(props.minValue !== undefined && v <= props.minValue
                    ? props.minValue
                    : props.maxValue !== undefined && v >= props.maxValue
                        ? props.maxValue
                        : v);
            }, readOnly: true, style: { caretColor: 'transparent' }, endAdornment: React.createElement(Grid, { container: true, style: {
                    width: '136px',
                    //width: '88px',
                    marginRight: '-12px',
                    marginTop: '-4px',
                } },
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(IconButton2, { color: (_a = props.color) !== null && _a !== void 0 ? _a : 'secondary', style: { padding: '8px', opacity: '0.5', }, disabled: (props.minValue !== undefined && value <= props.minValue) || props.decrementDisabled, onClick: _ => onChange(value - 1) },
                        React.createElement(RemoveCircle, { className: "MuiSvgIcon-root MuiSvgIcon-root-widget", color: (props.minValue !== undefined && value <= props.minValue) || props.decrementDisabled ? 'disabled' : (_b = props.color) !== null && _b !== void 0 ? _b : 'secondary' }))),
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(IconButton2, { color: (_c = props.color) !== null && _c !== void 0 ? _c : 'secondary', style: { padding: '8px', }, disabled: (props.maxValue !== undefined && value >= props.maxValue) || props.incrementDisabled, onClick: _ => onChange(value + 1) },
                        React.createElement(AddCircle, { className: "MuiSvgIcon-root MuiSvgIcon-root-widget", color: (props.maxValue !== undefined && value >= props.maxValue) || props.incrementDisabled ? 'disabled' : (_d = props.color) !== null && _d !== void 0 ? _d : 'secondary' })))), error: props.error })));
}
export function CustomSelect(props) {
    var _a, _b, _c, _d;
    const snackbar = useSnackbar();
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const getOptionValue = (o) => typeof props.valueSelector == 'function' ? props.valueSelector(o) : o[props.valueSelector];
    const getOptionText = (o) => typeof props.textSelector == 'function' ? props.textSelector(o) : o[props.textSelector];
    const getDisabledState = (o) => typeof props.disabledSelector == 'function' ? props.disabledSelector(o) : false;
    const handleToggle = () => {
        if (!props.disabled)
            setOpen((prevOpen) => !prevOpen);
        else
            setOpen(false);
    };
    const handleClose = (event) => {
        if (anchorRef.current && menuRef.current && menuRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    const handleListKeyDown = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    };
    const [selected, setSelected] = useState(props.value !== undefined ? [props.value] : []);
    const [searchText, setSearchText] = useState();
    const [searchOpen, setSearchOpen] = useState(false);
    const options = useMemo(() => searchText && searchOpen ? props.options.filter(o => getOptionText(o).toLowerCase().includes(searchText.toLowerCase())) : props.options, [props.options, searchOpen, searchText]);
    const valueLabel = useMemo(() => {
        let q = props.value === undefined ? undefined : options.find(p => getOptionValue(p) === props.value);
        if (q !== undefined && q !== null)
            return getOptionText(q);
        return '';
    }, [selected, options, props.textSelector, props.valueSelector, props.value]);
    const searchRef = useRef(null);
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);
    const MultiSelectMenu = React.createElement(MenuList, { ref: menuRef, autoFocusItem: open, id: "menu-list-grow", onKeyDown: handleListKeyDown, style: { maxHeight: (_a = props.heightPx) !== null && _a !== void 0 ? _a : 300, overflowY: 'auto' } }, options.map((o, i) => {
        let value = getOptionValue(o);
        let text = props.optionSelector && typeof props.optionSelector == 'function' ? props.optionSelector(o) : getOptionText(o);
        return (React.createElement(TpMenuItem, { selected: value == props.value, disabled: props.disabled || getDisabledState(o), dense: true, button: true, key: i, onClick: (e) => {
                props.onChange(value);
                setOpen(false);
                return;
            } },
            React.createElement(ListItemText, { primary: text })));
    }));
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, Object.assign({}, ((_b = props.boxProps) !== null && _b !== void 0 ? _b : {})),
            React.createElement("div", { style: { display: 'flex' }, ref: anchorRef },
                React.createElement(FormControl, { variant: "outlined", fullWidth: true, disabled: props.disabled },
                    React.createElement(OutlinedInput, { className: "pl-0", placeholder: props.label, disabled: props.disabled, value: valueLabel, onInput: (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }, readOnly: true, inputProps: { style: { caretColor: 'transparent', cursor: 'pointer' } }, style: { caretColor: 'transparent', fontSize: '1.2rem' }, "aria-controls": open ? 'menu-list-grow' : undefined, "aria-haspopup": "true", onClick: handleToggle, startAdornment: props.startAdornment, endAdornment: (_c = props.endAdornment) !== null && _c !== void 0 ? _c : React.createElement(TpArrowIcon2, { className: "MuiSvgIcon-root" }), error: props.error })),
                React.createElement(Popper, { open: open, anchorEl: anchorRef.current, role: undefined, transition: true, disablePortal: true, style: { zIndex: 9999, width: (_d = anchorRef.current) === null || _d === void 0 ? void 0 : _d.clientWidth, maxHeight: '300px' } }, ({ TransitionProps, placement }) => (React.createElement(Grow, Object.assign({}, TransitionProps, { style: { transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom', marginTop: placement === 'bottom' ? '2px' : 0, marginBottom: placement !== 'bottom' ? '2px' : 0 } }),
                    React.createElement(Paper, { elevation: 4 },
                        React.createElement(ClickAwayListener, { onClickAway: handleClose }, MultiSelectMenu)))))))));
}
